import { Loader2, CheckCircle, AlertCircle, FileSearch, Brain, Calculator } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  triggerPOExtraction,
  type ResolvedExtractionJob,
} from "@/hooks/usePOExtraction";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface POExtractionStatusProps {
  fileId: string;
  purchaseOrderId: string;
  organisationId: string;
  fileType?: string | null;
  extractionJob?: ResolvedExtractionJob | null;
  isLoading?: boolean;
  onComplete?: () => void;
}

const STATUS_CONFIG = {
  pending: { icon: Loader2, label: "Pending", progress: 10, spinning: true },
  extracting: { icon: FileSearch, label: "Extracting PDF", progress: 30, spinning: true },
  normalizing: { icon: Brain, label: "AI Processing", progress: 60, spinning: true },
  calculating: { icon: Calculator, label: "Calculating Funding", progress: 85, spinning: true },
  completed: { icon: CheckCircle, label: "Complete", progress: 100, spinning: false },
  failed: { icon: AlertCircle, label: "Failed", progress: 0, spinning: false },
};

export function POExtractionStatus({
  fileId,
  purchaseOrderId,
  organisationId,
  fileType,
  extractionJob: job,
  isLoading = false,
  onComplete,
}: POExtractionStatusProps) {
  const isPdf = !fileType || fileType === "pdf";

  if (!isPdf) return null;
  const [isTriggering, setIsTriggering] = useState(false);
  const hasAutoTriggered = useRef(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleTriggerExtraction = async () => {
    setIsTriggering(true);
    const result = await triggerPOExtraction(fileId, purchaseOrderId, organisationId);
    setIsTriggering(false);

    if (result.success) {
      toast({
        title: "Extraction started",
        description: "Processing your PO file...",
      });
      queryClient.invalidateQueries({ queryKey: ["po-extraction-job", fileId] });
    } else {
      toast({
        title: "Extraction failed",
        description: result.error || "Failed to start extraction",
        variant: "destructive",
      });
    }
  };

  const handleRetry = async () => {
    await handleTriggerExtraction();
  };

  // Auto-trigger extraction when no job exists
  useEffect(() => {
    if (!job && !isLoading && !isTriggering && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleTriggerExtraction();
    }
  }, [job, isLoading, isTriggering]);

  // Reset auto-trigger flag when fileId changes
  useEffect(() => {
    hasAutoTriggered.current = false;
  }, [fileId]);

  // Still loading the extraction job status
  if (isLoading) {
    return (
      <div className="p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <span className="font-medium">Loading extraction status...</span>
        </div>
      </div>
    );
  }

  // No job exists yet
  if (!job) {
    if (isTriggering) {
      return (
        <div className="p-4 border rounded-lg bg-card">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <span className="font-medium">Starting extraction...</span>
          </div>
          <Progress value={5} className="h-2 mb-2" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center p-6 border rounded-lg bg-muted/20">
        <FileSearch className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-4">
          Extract line items from this PO file
        </p>
        <Button onClick={handleTriggerExtraction} disabled={isTriggering}>
          Extract Line Items
        </Button>
      </div>
    );
  }

  const isFailed = job.status === "failed" || job.isStale;
  const config = isFailed
    ? STATUS_CONFIG.failed
    : STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  const failureMessage = job.isStale
    ? "Extraction is taking longer than expected. Please retry."
    : job.error_message || "An error occurred during extraction";

  // Call onComplete when job completes
  if (job.status === "completed" && !job.isStale && onComplete) {
    onComplete();
  }

  return (
    <div className="p-4 border rounded-lg bg-card">
      <div className="flex items-center gap-3 mb-3">
        <Icon
          className={`h-5 w-5 ${isFailed
            ? "text-destructive"
            : job.status === "completed"
              ? "text-green-600"
              : "text-primary"
            } ${config.spinning ? "animate-spin" : ""}`}
        />
        <span className="font-medium">
          {config.label}
        </span>
      </div>

      {!isFailed && (
        <Progress value={config.progress} className="h-2 mb-2" />
      )}

      {isFailed && (
        <div className="mt-2">
          <p className="text-sm text-destructive mb-3">
            {failureMessage}
          </p>
          <Button variant="outline" size="sm" onClick={handleRetry} disabled={isTriggering}>
            {isTriggering ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Retrying...
              </>
            ) : (
              "Retry Extraction"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
